/* ==========================================================================
   Holofoil — theme.js
   Global behaviour: money formatting, mobile menu drawer, mobile search,
   quantity inputs, auto-submit forms, slideshow.
   No dependencies. Everything degrades gracefully without JS.
   ========================================================================== */
(function () {
  'use strict';

  window.theme = window.theme || {};

  /* ------------------------------------------------------------------
     Money formatting (mirrors Shopify's money filter for JS updates)
     ------------------------------------------------------------------ */
  window.theme.formatMoney = function (cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || window.theme.moneyFormat || '${{amount}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = typeof precision === 'undefined' ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return 0;
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    var match = formatString.match(placeholderRegex);
    if (!match) return formatString;

    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return formatString.replace(placeholderRegex, value);
  };

  /* ------------------------------------------------------------------
     Generic drawer helper (mobile menu)
     ------------------------------------------------------------------ */
  function setupDrawer(drawerId, toggleSelector, bodyClass) {
    var drawer = document.getElementById(drawerId);
    if (!drawer) return null;

    var toggles = document.querySelectorAll(toggleSelector);
    var lastFocused = null;

    function open() {
      lastFocused = document.activeElement;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add(bodyClass);
      toggles.forEach(function (t) { t.setAttribute('aria-expanded', 'true'); });
      var focusable = drawer.querySelector('a, button:not([data-drawer-overlay]), input, select');
      if (focusable) focusable.focus({ preventScroll: true });
    }

    function close() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove(bodyClass);
      toggles.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
      if (lastFocused) lastFocused.focus({ preventScroll: true });
    }

    toggles.forEach(function (toggle) {
      toggle.addEventListener('click', function (event) {
        event.preventDefault();
        if (drawer.classList.contains('is-open')) close();
        else open();
      });
    });

    drawer.addEventListener('click', function (event) {
      if (event.target.closest('[data-drawer-close]')) {
        event.preventDefault();
        close();
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });

    return { open: open, close: close };
  }

  /* ------------------------------------------------------------------
     Mobile search toggle
     ------------------------------------------------------------------ */
  function setupMobileSearch() {
    var toggle = document.querySelector('[data-search-toggle]');
    var bar = document.getElementById('MobileSearch');
    if (!toggle || !bar) return;

    toggle.addEventListener('click', function () {
      var isOpen = bar.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      if (isOpen) {
        var input = bar.querySelector('input[type="search"]');
        if (input) input.focus();
      }
    });
  }

  /* ------------------------------------------------------------------
     Quantity +/- buttons (event delegation, works in drawer refreshes)
     ------------------------------------------------------------------ */
  function setupQuantityButtons() {
    document.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-qty-change]');
      if (!btn) return;
      event.preventDefault();

      var wrapper = btn.closest('.qty');
      if (!wrapper) return;
      var input = wrapper.querySelector('input');
      if (!input) return;

      var current = parseInt(input.value, 10);
      if (isNaN(current)) current = 1;
      var min = parseInt(input.getAttribute('min') || '0', 10);
      var next = btn.getAttribute('data-qty-change') === 'up' ? current + 1 : current - 1;
      if (next < min) next = min;
      if (next === current) return;

      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ------------------------------------------------------------------
     Auto-submit forms (cart page quantities, localization selector)
     ------------------------------------------------------------------ */
  function setupAutoSubmit() {
    document.addEventListener('change', function (event) {
      var form = event.target.closest('form[data-auto-submit]');
      if (!form) return;
      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else form.submit();
    });
  }

  /* ------------------------------------------------------------------
     Hero slideshow (scroll-snap based; usable without JS)
     ------------------------------------------------------------------ */
  function Slideshow(root) {
    var track = root.querySelector('[data-slideshow-track]');
    var slides = root.querySelectorAll('.slide');
    if (!track || slides.length < 2) return;

    var dots = root.querySelectorAll('[data-slide-dot]');
    var autoplay = root.getAttribute('data-autoplay') === 'true';
    var speed = parseInt(root.getAttribute('data-speed') || '6', 10) * 1000;
    var timer = null;
    var current = 0;
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function goTo(index) {
      if (index < 0) index = slides.length - 1;
      if (index >= slides.length) index = 0;
      track.scrollTo({ left: track.clientWidth * index, behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    function setCurrent(index) {
      current = index;
      dots.forEach(function (dot, i) {
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }

    var scrollTimeout;
    track.addEventListener('scroll', function () {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(function () {
        var index = Math.round(track.scrollLeft / track.clientWidth);
        if (index !== current) setCurrent(index);
      }, 80);
    }, { passive: true });

    root.addEventListener('click', function (event) {
      var next = event.target.closest('[data-slide-next]');
      var prev = event.target.closest('[data-slide-prev]');
      var dot = event.target.closest('[data-slide-dot]');
      if (next) { goTo(current + 1); restart(); }
      if (prev) { goTo(current - 1); restart(); }
      if (dot) { goTo(parseInt(dot.getAttribute('data-slide-dot'), 10)); restart(); }
    });

    function start() {
      if (!autoplay || reduceMotion) return;
      stop();
      timer = setInterval(function () { goTo(current + 1); }, speed);
    }

    function stop() {
      if (timer) clearInterval(timer);
      timer = null;
    }

    function restart() {
      stop();
      start();
    }

    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', start);
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop();
      else start();
    });

    setCurrent(0);
    start();
  }

  /* ------------------------------------------------------------------
     Init
     ------------------------------------------------------------------ */
  function init() {
    setupDrawer('MobileMenuDrawer', '[data-menu-toggle]', 'menu-open');
    setupMobileSearch();
    setupQuantityButtons();
    setupAutoSubmit();
    document.querySelectorAll('[data-slideshow]').forEach(function (el) {
      Slideshow(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
